import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button, ModalContentContainer, TextField } from '@/components';
import { useForm } from '@/hooks';
import { AnalyticsService, EVENTS } from '@/services';
import {
  useCompleteAccountRecoveryMutation,
  useValidateRecoveryVerificationCodeQuery,
} from '@/store';

import { PageContainer } from '../components';

const STEPS = {
  INITIAL: 0,
  UPDATE_PASSWORD: 1,
  SUCCESS: 2,
  ERROR: 3,
} as const;

export const RecoverAccountPage = () => {
  const location = useLocation();
  const [step, setStep] = useState<(typeof STEPS)[keyof typeof STEPS]>(
    STEPS.INITIAL,
  );
  const { form, errors, setErrors, onChange, validateForm } = useForm({
    newPassword: '',
    confirmPassword: '',
  });
  const { verification_code: verificationCode } = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );

  const { data, isLoading } = useValidateRecoveryVerificationCodeQuery(
    {
      verificationCode,
    },
    {
      skip: !verificationCode,
    },
  );

  const [submit, { isSuccess, isError }] = useCompleteAccountRecoveryMutation();

  useEffect(() => {
    AnalyticsService.logEvent(EVENTS.ACCOUNT_RECOVERY_EVENTS.RECOVERY_STEP, {
      step: 'welcome',
    });
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setStep(STEPS.SUCCESS);
      AnalyticsService.logEvent(EVENTS.ACCOUNT_RECOVERY_EVENTS.RECOVERY_STEP, {
        step: "You're all set",
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setStep(STEPS.ERROR);
      AnalyticsService.logEvent(EVENTS.ACCOUNT_RECOVERY_EVENTS.RECOVERY_STEP, {
        step: 'error',
      });
    }
  }, [isError]);

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    try {
      await submit({
        password: form.newPassword.trim(),
        verificationCode,
      }).unwrap();
      setStep(2);
    } catch (error) {
      const { status, data } = error as {
        status: number;
        data: { message: string };
      };
      if (status === 400 && data?.message) {
        setErrors(prev => ({
          ...prev,
          password: data?.message,
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          password:
            'Something went wrong. Please try again or contact support.',
        }));
      }
    }
  };

  return (
    <PageContainer hideCertificationInfo>
      <ModalContentContainer>
        {isLoading && (
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        )}
        {!data?.valid && !isLoading && (
          <>
            <Typography variant="h1">Invalid Verification Code</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              The verification code you entered is invalid or has expired.
              <br />
              <br />
              Please check your email for the correct code or request a new one.
            </Typography>
            <Button
              variant="primary"
              label="Request New Code"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                window.location.href = '/forgot-password';
              }}
            />
          </>
        )}
        {data?.valid && step === STEPS.INITIAL && (
          <>
            <Typography variant="h1">Account Recovery</Typography>
            <Typography variant="body1" sx={{ mt: 6 }}>
              You recently asked to recover your LumiQ account. It looks like
              your previous login email was:{' '}
              <strong>{data?.identity_email}</strong>
              <br />
              <br />
              Would you like to switch it to{' '}
              <strong>{data?.recovery_email}</strong> instead?
              <br />
              <br />
              You will retain your listening data, and all your earned
              certificates.
            </Typography>
            <Button
              variant="primary"
              label="Continue with Recovery"
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                setStep(STEPS.UPDATE_PASSWORD);
                AnalyticsService.logEvent(
                  EVENTS.ACCOUNT_RECOVERY_EVENTS.RECOVERY_STEP,
                  {
                    step: 'Set up your account',
                  },
                );
              }}
            />
            <Box sx={{ mt: 2 }}>
              <Link
                sx={{
                  textDecoration: 'none',
                  color: 'secondary.main',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={() => {
                  window.Intercom?.('show');
                }}>
                Contact Support
              </Link>
            </Box>
          </>
        )}
        {data?.valid && step === STEPS.UPDATE_PASSWORD && (
          <>
            <Typography variant="h1">Create a Password</Typography>
            <TextField
              label="Email"
              fullWidth
              value={data?.recovery_email}
              disabled
              sx={{ mt: 6 }}
            />
            <TextField
              id="newPassword"
              label="Password"
              fullWidth
              placeholder="Enter your password"
              type="password"
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              value={form.newPassword}
              sx={{ mt: 2 }}
              onChange={onChange}
            />
            <TextField
              id="confirmPassword"
              label="Confirm Password"
              fullWidth
              placeholder="Re-enter your password"
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              value={form.confirmPassword}
              sx={{ mt: 2 }}
              onChange={onChange}
            />
            <Button
              variant="primary"
              fullWidth
              label="Update Account"
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={onSubmit}
            />
            <Link
              sx={{
                textDecoration: 'none',
                color: 'secondary.main',
                '&:hover': {
                  cursor: 'pointer',
                },
                mt: 2,
              }}
              onClick={() => {
                setStep(STEPS.INITIAL);
              }}>
              Go Back
            </Link>
          </>
        )}
        {data?.valid && step === STEPS.SUCCESS && (
          <>
            <Typography variant="h1">Success!</Typography>
            <Typography variant="body1" sx={{ mt: 3 }}>
              Your password has been updated.
              <br />
              <br />
              Next time you Log In to LumiQ you&apos;ll use{' '}
              <strong>{data?.recovery_email}</strong> to access your account.
            </Typography>
            <Button
              variant="primary"
              label="Explore LumiQ"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                window.location.href = '/login';
              }}
            />
          </>
        )}
        {data?.valid && step === STEPS.ERROR && (
          <>
            <Typography variant="h1">Error</Typography>
            <Typography variant="body1" sx={{ mt: 3 }}>
              There was an error updating your password. Please try again or
              contact support.
            </Typography>
            <Button
              variant="primary"
              label="Go Back"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                setStep(STEPS.INITIAL);
              }}
            />
          </>
        )}
      </ModalContentContainer>
    </PageContainer>
  );
};
